import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Image, Award, DollarSign, FileText, Upload, X, Loader2, CheckCircle, AlertCircle, Tag, Save, User, Landmark
} from 'lucide-react';
import { SA_BANKS } from '../../config/banks';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EXPERIENCE_LEVELS = [
  { value: 'beginner', label: 'Beginner (0-2 yrs)' },
  { value: 'intermediate', label: 'Intermediate (3-7 yrs)' },
  { value: 'expert', label: 'Expert (8+ yrs)' },
];

const PRICING_PRESETS = [
  'R200 - R500',
  'R500 - R1,500',
  'R1,500 - R5,000',
  'R5,000 - R15,000',
  'R15,000+',
  'Custom',
];

const ProfileCompletion = () => {
  const { pro, proId } = useOutletContext();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    profile_image: '',
    portfolio_images: [],
    certifications: [],
    pricing_range: '',
    business_description: '',
    bank_name: '',
    bank_account_number: '',
    bank_account_holder: ''
  });
  const [structuredServices, setStructuredServices] = useState([]);
  const [serviceCatalog, setServiceCatalog] = useState([]);
  const [servicesSaving, setServicesSaving] = useState(false);
  const [servicesSaved, setServicesSaved] = useState(false);
  const [servicesError, setServicesError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (pro) {
      setFormData({
        profile_image: pro.profile_image || pro.image || '',
        portfolio_images: pro.portfolio_images || [],
        certifications: pro.certifications || [],
        pricing_range: pro.pricing_range || '',
        business_description: pro.business_description || '',
        bank_name: pro.bank_name || '',
        bank_account_number: pro.bank_account_number || '',
        bank_account_holder: pro.bank_account_holder || ''
      });
      // Normalise structured services (handles legacy string arrays too)
      const rawServices = pro.services || [];
      const normalized = rawServices.map(s =>
        typeof s === 'string'
          ? { service_id: s, experience_level: 'intermediate', years_experience: 3, pricing_range: 'R500 - R1,500' }
          : {
              service_id: s.service_id,
              experience_level: s.experience_level || 'intermediate',
              years_experience: s.years_experience ?? 3,
              pricing_range: s.pricing_range || 'R500 - R1,500',
            }
      );
      setStructuredServices(normalized);
    }
  }, [pro]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await axios.get(`${API}/services/popular`);
        setServiceCatalog(res.data);
      } catch (e) {
        console.error('Error fetching services:', e);
      }
    };
    fetchServices();
  }, []);

  const getServiceName = (slug) =>
    serviceCatalog.find(s => s.slug === slug)?.name || slug;

  const updateStructuredService = (slug, field, value) => {
    setStructuredServices(prev => prev.map(s =>
      s.service_id === slug
        ? { ...s, [field]: field === 'years_experience' ? (parseInt(value, 10) || 0) : value }
        : s
    ));
    setServicesSaved(false);
  };

  const saveStructuredServices = async () => {
    setServicesError('');
    setServicesSaving(true);
    setServicesSaved(false);
    try {
      // Validate
      for (const svc of structuredServices) {
        if (!svc.pricing_range || !svc.pricing_range.trim()) {
          throw new Error(`${getServiceName(svc.service_id)}: pricing range is required`);
        }
        if (svc.years_experience < 0) {
          throw new Error(`${getServiceName(svc.service_id)}: years of experience must be 0 or higher`);
        }
      }
      await axios.put(
        `${API}/professionals/${proId}/services`,
        { services: structuredServices },
        { withCredentials: true }
      );
      setServicesSaved(true);
      setTimeout(() => setServicesSaved(false), 3000);
    } catch (e) {
      setServicesError(e.response?.data?.detail || e.message || 'Failed to update services');
    } finally {
      setServicesSaving(false);
    }
  };

  const calculateCompleteness = () => {
    let completeness = 40; // Base from Step 1
    if (formData.profile_image) completeness += 10;
    if (formData.portfolio_images.length > 0) completeness += 25;
    if (formData.certifications.length > 0) completeness += 15;
    if (formData.business_description) completeness += 10;
    return Math.min(completeness, 100);
  };

  const PORTFOLIO_LIMIT = 5;

  const handleAddImage = () => {
    if (formData.portfolio_images.length >= PORTFOLIO_LIMIT) {
      setError(`You can upload at most ${PORTFOLIO_LIMIT} portfolio images.`);
      return;
    }
    const url = prompt('Paste an image URL (e.g., from Unsplash, Imgur, your website):');
    if (url && url.trim()) {
      setFormData(prev => ({
        ...prev,
        portfolio_images: [...prev.portfolio_images, url.trim()].slice(0, PORTFOLIO_LIMIT)
      }));
      setError('');
    }
  };

  const handleSetProfileImage = () => {
    const url = prompt('Paste a profile image URL (a clear photo of you or your business logo):', formData.profile_image);
    if (url !== null) {
      setFormData(prev => ({ ...prev, profile_image: url.trim() }));
    }
  };

  const handleRemoveProfileImage = () => {
    setFormData(prev => ({ ...prev, profile_image: '' }));
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      portfolio_images: prev.portfolio_images.filter((_, i) => i !== index)
    }));
  };

  const handleAddCertification = () => {
    const cert = prompt('Enter certification name:');
    if (cert) {
      setFormData(prev => ({
        ...prev,
        certifications: [...prev.certifications, cert]
      }));
    }
  };

  const handleRemoveCertification = (index) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.put(
        `${API}/professionals/${proId}/complete-profile`,
        formData,
        { withCredentials: true }
      );
      setSuccess(true);
      setTimeout(() => {
        navigate(`/dashboard/${proId}/performance`);
      }, 1500);
    } catch (e) {
      setError('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const completeness = calculateCompleteness();

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Complete Your Profile</h1>
        <p className="text-sm text-gray-500 mt-1">Step 2: Add portfolio, certifications, and more (Optional)</p>
      </div>

      {/* Completeness Bar */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6" data-testid="completeness-card">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-gray-700">Profile Completeness</span>
          <span className="text-lg font-bold text-[#37b34a]" data-testid="completeness-pct">{completeness}%</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-3">
          <div
            className="bg-[#37b34a] h-3 rounded-full transition-all duration-300"
            style={{ width: `${completeness}%` }}
          />
        </div>
        <p className="text-xs text-gray-500 mt-2">
          {completeness === 100 ? 'Profile 100% complete! 🎉' : 'Complete all fields to reach 100%'}
        </p>
      </div>

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2 mb-6">
          <CheckCircle size={16} />
          Profile updated successfully!
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2 mb-6">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* My Services — Structured editing */}
        <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="my-services-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Tag size={20} className="text-gray-600" />
              <h2 className="text-lg font-bold text-gray-900">My Services</h2>
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">
                {structuredServices.length} service(s)
              </span>
            </div>
            <button
              type="button"
              onClick={saveStructuredServices}
              disabled={servicesSaving || structuredServices.length === 0}
              data-testid="save-services-btn"
              className="text-sm bg-[#37b34a] hover:bg-[#2d9a3e] disabled:bg-gray-300 text-white font-semibold px-4 py-1.5 rounded-lg flex items-center gap-2 transition-colors"
            >
              {servicesSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save
            </button>
          </div>

          {servicesError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm flex items-center gap-2 mb-3">
              <AlertCircle size={14} /> {servicesError}
            </div>
          )}
          {servicesSaved && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-2 rounded-lg text-sm flex items-center gap-2 mb-3">
              <CheckCircle size={14} /> Services updated successfully!
            </div>
          )}

          {structuredServices.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6 border border-dashed border-gray-200 rounded-lg">
              No services registered yet. Contact support to add services.
            </p>
          ) : (
            <div className="space-y-3">
              {structuredServices.map((svc) => (
                <div
                  key={svc.service_id}
                  data-testid={`edit-service-card-${svc.service_id}`}
                  className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                >
                  <h3 className="text-sm font-bold text-gray-900 mb-3">{getServiceName(svc.service_id)}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Experience level</label>
                      <select
                        value={svc.experience_level}
                        onChange={(e) => updateStructuredService(svc.service_id, 'experience_level', e.target.value)}
                        data-testid={`edit-exp-level-${svc.service_id}`}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-[#37b34a] focus:border-transparent outline-none"
                      >
                        {EXPERIENCE_LEVELS.map(lvl => (
                          <option key={lvl.value} value={lvl.value}>{lvl.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Years of experience</label>
                      <input
                        type="number"
                        min="0"
                        max="60"
                        value={svc.years_experience}
                        onChange={(e) => updateStructuredService(svc.service_id, 'years_experience', e.target.value)}
                        data-testid={`edit-years-${svc.service_id}`}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-[#37b34a] focus:border-transparent outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Pricing range</label>
                      <select
                        value={PRICING_PRESETS.includes(svc.pricing_range) ? svc.pricing_range : 'Custom'}
                        onChange={(e) => {
                          const v = e.target.value;
                          updateStructuredService(svc.service_id, 'pricing_range', v === 'Custom' ? '' : v);
                        }}
                        data-testid={`edit-pricing-select-${svc.service_id}`}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-[#37b34a] focus:border-transparent outline-none"
                      >
                        {PRICING_PRESETS.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                      {(!PRICING_PRESETS.includes(svc.pricing_range) || svc.pricing_range === 'Custom') && (
                        <input
                          type="text"
                          value={svc.pricing_range}
                          onChange={(e) => updateStructuredService(svc.service_id, 'pricing_range', e.target.value)}
                          data-testid={`edit-pricing-${svc.service_id}`}
                          placeholder="e.g., R800 - R2,500"
                          className="mt-1 w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-[#37b34a] focus:border-transparent outline-none"
                        />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Profile Image — single avatar/logo */}
        <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="profile-image-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <User size={20} className="text-gray-600" />
              <h2 className="text-lg font-bold text-gray-900">Profile Photo</h2>
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">+10%</span>
            </div>
            <button
              type="button"
              onClick={handleSetProfileImage}
              data-testid="profile-image-set-btn"
              className="text-sm text-[#37b34a] hover:text-[#2d9a3e] font-semibold flex items-center gap-1"
            >
              <Upload size={14} /> {formData.profile_image ? 'Replace' : 'Add Photo'}
            </button>
          </div>
          {formData.profile_image ? (
            <div className="flex items-center gap-4">
              <div className="relative">
                <img
                  src={formData.profile_image}
                  alt="Profile"
                  data-testid="profile-image-preview"
                  className="w-24 h-24 object-cover rounded-full border-4 border-gray-100"
                  onError={(e) => { e.currentTarget.style.opacity = 0.3; }}
                />
                <button
                  type="button"
                  onClick={handleRemoveProfileImage}
                  data-testid="profile-image-remove-btn"
                  className="absolute -top-1 -right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 shadow-sm"
                  aria-label="Remove profile photo"
                >
                  <X size={12} />
                </button>
              </div>
              <p className="text-xs text-gray-500 max-w-md break-all">{formData.profile_image}</p>
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-6 border border-dashed border-gray-200 rounded-lg">
              Add a clear profile photo or your business logo. Customers trust pros with photos.
            </p>
          )}
        </div>

        {/* Portfolio Images */}
        <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="portfolio-images-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Image size={20} className="text-gray-600" />
              <h2 className="text-lg font-bold text-gray-900">Portfolio Images</h2>
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">
                {formData.portfolio_images.length}/{PORTFOLIO_LIMIT}
              </span>
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">+25%</span>
            </div>
            <button
              type="button"
              onClick={handleAddImage}
              disabled={formData.portfolio_images.length >= PORTFOLIO_LIMIT}
              data-testid="portfolio-add-btn"
              className="text-sm text-[#37b34a] hover:text-[#2d9a3e] disabled:text-gray-300 disabled:cursor-not-allowed font-semibold flex items-center gap-1"
            >
              <Upload size={14} /> Add Image
            </button>
          </div>
          {formData.portfolio_images.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">No images added yet. Showcase your work — up to {PORTFOLIO_LIMIT} images.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="portfolio-images-grid">
              {formData.portfolio_images.map((url, idx) => (
                <div key={idx} className="relative group">
                  <img
                    src={url}
                    alt={`Portfolio ${idx + 1}`}
                    data-testid={`portfolio-image-${idx}`}
                    className="w-full h-32 object-cover rounded-lg bg-gray-50"
                    onError={(e) => { e.currentTarget.style.opacity = 0.3; }}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(idx)}
                    data-testid={`portfolio-remove-${idx}`}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label={`Remove portfolio image ${idx + 1}`}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Certifications */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Award size={20} className="text-gray-600" />
              <h2 className="text-lg font-bold text-gray-900">Certifications</h2>
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">+15%</span>
            </div>
            <button
              type="button"
              onClick={handleAddCertification}
              className="text-sm text-[#37b34a] hover:text-[#2d9a3e] font-semibold"
            >
              + Add
            </button>
          </div>
          {formData.certifications.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">No certifications added yet.</p>
          ) : (
            <div className="space-y-2">
              {formData.certifications.map((cert, idx) => (
                <div key={idx} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3">
                  <span className="text-sm text-gray-700">{cert}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveCertification(idx)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pricing Range */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign size={20} className="text-gray-600" />
            <h2 className="text-lg font-bold text-gray-900">Pricing Range</h2>
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">+10%</span>
          </div>
          <input
            type="text"
            value={formData.pricing_range}
            onChange={(e) => setFormData({ ...formData, pricing_range: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#37b34a] focus:border-transparent outline-none"
            placeholder="e.g., R500 - R2000 per project"
          />
        </div>

        {/* Business Description */}
        <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="business-description-section">
          <div className="flex items-center gap-2 mb-4">
            <FileText size={20} className="text-gray-600" />
            <h2 className="text-lg font-bold text-gray-900">Business Description</h2>
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">+10%</span>
          </div>
          <textarea
            value={formData.business_description}
            onChange={(e) => setFormData({ ...formData, business_description: e.target.value })}
            data-testid="business-description-textarea"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#37b34a] focus:border-transparent outline-none"
            placeholder="Tell customers about your business, experience, and what makes you unique..."
            rows={5}
          />
        </div>

        {/* Bank Details — optional, for future payouts */}
        <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="bank-details-section">
          <div className="flex items-center gap-2 mb-4">
            <Landmark size={20} className="text-gray-600" />
            <h2 className="text-lg font-bold text-gray-900">Bank Details</h2>
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">Optional</span>
          </div>
          <p className="text-xs text-gray-500 mb-4">We're not processing payouts yet — adding this now just saves you a step later.</p>
          <div className="grid sm:grid-cols-3 gap-3">
            <select
              value={formData.bank_name}
              onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
              data-testid="profile-bank-name-select"
              className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-[#37b34a] focus:border-transparent outline-none"
            >
              <option value="">Select a bank…</option>
              {SA_BANKS.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
            <input
              type="text"
              value={formData.bank_account_holder}
              onChange={(e) => setFormData({ ...formData, bank_account_holder: e.target.value })}
              data-testid="profile-bank-account-holder-input"
              placeholder="Account holder name"
              className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#37b34a] focus:border-transparent outline-none"
            />
            <input
              type="text"
              value={formData.bank_account_number}
              onChange={(e) => setFormData({ ...formData, bank_account_number: e.target.value.replace(/[^0-9]/g, '') })}
              data-testid="profile-bank-account-number-input"
              placeholder="Account number"
              className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#37b34a] focus:border-transparent outline-none"
            />
          </div>
        </div>

        {/* Submit */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => navigate(`/dashboard/${proId}/performance`)}
            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold px-6 py-3 rounded-lg transition-colors"
          >
            Skip for Now
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 bg-[#37b34a] hover:bg-[#2d9a3e] disabled:bg-gray-300 text-white font-semibold px-6 py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {loading ? (
              <><Loader2 size={18} className="animate-spin" /> Saving...</>
            ) : (
              'Save Profile'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProfileCompletion;
